import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { workflowRulesService } from '../../api/workflowRulesService';
import type { WorkflowRule } from '../../api/workflowRulesService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type BadgeVariant = 'success' | 'warning' | 'info' | 'default';

const RULE_BADGE_VARIANTS: Record<WorkflowRule['rule_type'], BadgeVariant> = {
  activate: 'success',
  inactivate: 'warning',
  replace: 'info',
};

function getRuleBadgeVariant(ruleType: WorkflowRule['rule_type']): BadgeVariant {
  return RULE_BADGE_VARIANTS[ruleType] ?? 'default';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface WorkflowRulesPanelProps {
  readonly workflowSlug: string;
}

export function WorkflowRulesPanel({ workflowSlug }: WorkflowRulesPanelProps) {
  const navigate = useNavigate();

  const listPage = useAdminListPage<WorkflowRule, string>({
    queryKey: ['admin', 'workflows', workflowSlug, 'rules'],
    queryFn: () => workflowRulesService.list(workflowSlug),
    deleteFn: (ruleId) => workflowRulesService.remove(workflowSlug, ruleId),
    getDeleteId: (rule) => rule.id,
    extraInvalidateKeys: [
      ['admin', 'workflows', workflowSlug],
    ],
  });

  return (
    <AdminListPage<WorkflowRule>
      variant="panel"
      title="Rules"
      loadingLabel="Loading rules..."
      errorTitle="Failed to load rules"
      errorFallback="An unexpected error occurred."
      emptyTitle="No rules found"
      emptyDescription="Get started by creating your first tenant rule for this workflow."
      createLabel="Create Rule"
      createButtonSize="sm"
      onCreateClick={() => navigate(`/admin/workflows/${workflowSlug}/rules/create`)}
      items={listPage.items}
      getRowKey={(rule) => rule.id}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Rule"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(rule) => `${rule.tenant.name} - ${rule.target_step.name}`}
      deleteNoun="rule"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      isDeleteError={listPage.isDeleteError}
      deleteError={listPage.deleteError}
      renderHead={() => (
        <>
          <Table.HeaderCell>Tenant</Table.HeaderCell>
          <Table.HeaderCell>Rule Type</Table.HeaderCell>
          <Table.HeaderCell>Target Step</Table.HeaderCell>
          <Table.HeaderCell>Active</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(rule) => (
        <>
          <Table.Cell>
            <Text weight="medium">{rule.tenant.name}</Text>
          </Table.Cell>
          <Table.Cell>
            <Badge
              variant={getRuleBadgeVariant(rule.rule_type)}
              size="sm"
            >
              {rule.rule_type}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm">
              {rule.target_step.sort_order}. {rule.target_step.name}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Badge
              variant={rule.is_active ? 'success' : 'default'}
              size="sm"
            >
              {rule.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Pencil} size="xs" color="inherit" />}
                onClick={() => navigate(`/admin/workflows/${workflowSlug}/rules/${rule.id}/edit`)}
                aria-label={`Edit rule for ${rule.tenant.name}`}
              />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Trash2} size="xs" />}
                onClick={() => listPage.setDeleteTarget(rule)}
                aria-label={`Delete rule for ${rule.tenant.name}`}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
